import { getUrlLocaleCode } from '~/lang/locales'
import { getGeoPointFromWKTPoint } from '~/utility/geo/GeoLocation'
import { langFallback$Fetch } from '../../../../../lang/utils/langfetch'
import { LocationSuggestionHierarchy } from '../domain/LocationSuggestions'
import { HierarchyResponse, hierarchyResponseTypeSchema } from '../schemas'
import {
  LocationSuggestionsFetchError,
  LocationSuggestionsValidationError,
} from './LocationSuggestionsError'

// Default timeout for search suggestions (10 seconds - should be fast)
const SEARCH_SUGGESTIONS_TIMEOUT = 10000

export default class FetchLocationHierarchySuggestions {
  async fetch(
    query: string,
    langCode: string,
  ): Promise<LocationSuggestionHierarchy[]> {
    const url = `/search/predictive/hierarchy/?q=${query}`
    let response: HierarchyResponse[]

    // Step 1: Fetch the data
    try {
      response = await langFallback$Fetch<HierarchyResponse[]>(langCode, url, {
        timeout: SEARCH_SUGGESTIONS_TIMEOUT,
      })
    } catch (error: any) {
      // Silently return empty array for abort errors - they're intentional cancellations
      // This is especially common in predictive search when users type quickly
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call -- TODO: Add proper Error type
      if (error?.name === 'AbortError' || error?.message?.includes('aborted')) {
        return []
      }

      // Throw custom fetch error - don't log here, let the store handle logging to avoid duplicates
      throw new LocationSuggestionsFetchError(url, query, 'hierarchy', error)
    }

    // Step 2: Validate with Zod schema
    try {
      const data = hierarchyResponseTypeSchema.array().parse(response)
      return this.getParsedHierarchies(
        data,
        langCode,
      ) as LocationSuggestionHierarchy[]
    } catch (error: any) {
      const validationError = new LocationSuggestionsValidationError(
        url,
        query,
        'hierarchy',
        response,
        error,
      )
      // Don't log here - let the store handle logging to avoid duplicates
      throw validationError
    }
  }

  private getParsedHierarchies(
    hierarchies: HierarchyResponse[],
    langCode: string,
  ) {
    return hierarchies.map((hierarchy) => {
      return {
        ...hierarchy,
        path: this.getHierarchyPathFromUrl(hierarchy.url, langCode),
        bookableCount: hierarchy.bookable_count_number,
        bookableCountDisplay: hierarchy.bookable_count,
        bookableString: hierarchy.bookable_string,
        countryCode: hierarchy.country_iso,
        point: this.mockPoint(hierarchy),
        searchRadius: hierarchy.search_radius,
      }
    })
  }

  private getHierarchyPathFromUrl(url: string, locale: string) {
    const prefix = getUrlLocaleCode(locale)
    const urlPrefix = `/${prefix}/`
    let outputUrl = url.slice()
    if (url.startsWith(urlPrefix)) {
      outputUrl = outputUrl.replace(urlPrefix, '/')
    }
    return outputUrl.split('/').slice(2).join('/')
  }

  private mockPoint(hierarchy: HierarchyResponse) {
    return hierarchy.type === 3
      ? getGeoPointFromWKTPoint('POINT(0 0)')
      : undefined
  }
}
